/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Sparkle16Regular } from '@fluentui/react-icons';
import { Observer, observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { EntityBrush } from 'src/customText/verticals/extractionLabeler/EntityBrush';
import { ExtractionEntityDropdown } from 'src/customText/verticals/extractionLabeler/ExtractionEntityDropdown';
import {
    EntityLabel,
    ExtractionLabelerTokenStore,
    ExtractionLabelerUIStore,
    PartialEntityLabel
} from 'src/customText/verticals/extractionLabeler/types';
import { findLabel, onTokenRender } from 'src/customText/verticals/extractionLabeler/utils';
import { Label } from 'src/modules/labeling/types/Label';
import { AnnotationData, ITokenRendererProps, Labeler, LabelerRef } from 'src/packages/react-text-annotator/lib/esm';

export type LabelingMode = { mode: 'brush'; entityId: string } | { mode: 'menu' };

export type ExtractionLabelerProps = {
    text: string;
    isRtl?: boolean;
    className?: string;
    mode: LabelingMode;
    labels: EntityLabel[];
    schema: EntityStore[];
    isWordTokenized?: boolean;
    onInvalidSelection?: () => void;
    isExtractionLabelerDisabled?: boolean;
    onCreateNewEntity: (name: string) => void;
    onAddLabel: (label: PartialEntityLabel) => void;
    onRemoveLabel: (label: PartialEntityLabel) => void;
    onConfirmAutoTags: (labels: PartialEntityLabel[]) => void;
};

const messages = defineMessages({
    gptLabeled: { id: 'customText.extractionLabeler.gptLabeled', defaultMessage: 'Labeled by AI' },
    autoLabeled: { id: 'customText.extractionLabeler.autoLabeled', defaultMessage: 'Auto-labeled' }
});

const theme = getScopedTheme('ExtractionLabeler');
const { getClassName, styleDiv } = getStylistV2('ExtractionLabeler');
const gptLabelIconClassName = getClassName('gptLabelIconClassName', {
    width: '12px',
    fillOpacity: 1,
    height: '12px',
    shapeRendering: 'auto',
    verticalAlign: 'text-bottom',
    fill: `${theme.gptIconColor} !important`
});
const LabelerWrapper = styleDiv(
    'LabelerWrapper',
    (_theme, cssVars) => ({
        background: _theme.containerBackground,
        padding: cssVars.padding
    }),
    {
        cssVars: { padding: '' },
        cssVarsSetter: ({ isRtl }: { isRtl: boolean }) => ({
            padding: `0 ${isRtl ? '32px' : 0} 0 ${isRtl ? 0 : '32px'}`
        })
    }
);

const transformLabels = (labels: EntityLabel[]): Label[] =>
    labels.map(l => ({
        depth: 1,
        renderingLevel: 1,
        renderedAs: 'line',
        end: l.endCharIndex,
        start: l.startCharIndex,
        intrinsicEnd: l.endCharIndex,
        intrinsicStart: l.startCharIndex,
        viewOptions: { isDashed: l.labelType !== 'normal' },
        entity: { roles: [], id: l.entity.id, name: l.entity.name, typeId: l.entity.typeId }
    }));

export const ExtractionLabeler = observer((props: ExtractionLabelerProps) => {
    const {
        text,
        mode,
        labels,
        schema,
        className,
        onAddLabel,
        isRtl = false,
        onRemoveLabel,
        onConfirmAutoTags,
        onCreateNewEntity,
        onInvalidSelection = noop,
        isWordTokenized = false,
        isExtractionLabelerDisabled = false
    } = props;

    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const labelerRef = React.useRef<LabelerRef>();
    const wrapperRef = React.useRef<HTMLDivElement>();
    const [store] = React.useState(() => new ExtractionLabelerUIStore());

    const onRenderAnnotationName = (label: Label) => (
        <LuisText variant="xSmall" styles={{ root: { textOverflow: 'ellipsis', overflow: 'hidden' } }}>
            {findLabel(label, labels).labelType === 'gptPredicted' && (
                <Sparkle16Regular className={gptLabelIconClassName} color={theme.gptIconColor} />
            )}
            {`${label.entity.name} - ${localize(
                findLabel(label, labels).labelType === 'gptPredicted' ? messages.gptLabeled : messages.autoLabeled
            )}`}
        </LuisText>
    );

    const transformedLabels = transformLabels(labels);
    const endIndicesLabelMap = transformedLabels.reduce((m, e) => m.set(e.end, e), new Map<number, Label>());
    const resolveShapeColor = React.useCallback((label: Label) => schema.find(e => e.id === label.entity.id)?.color, [schema]);
    const annotations = transformedLabels.map(label => ({
        ...transformLabelToAnnotation(label, null, resolveShapeColor),
        dashesLength: findLabel(label, labels).labelType === 'gptPredicted' ? '20,20' : '2,2',
        onRenderName: findLabel(label, labels).labelType !== 'normal' ? () => onRenderAnnotationName(label) : undefined
    }));
    const { canWrite } = mapUserRoleToUserActions(rootStore.serviceKey.userRole);

    useDocumentEventHandler('wheel', ev => {
        if ((ev.target as HTMLElement).className?.includes?.('ContextualMenu')) {
            return;
        }

        if (store.dropdownData) {
            store.setDropdownData(null);
        }
    });

    const handleSelect = React.useCallback(
        (selectionStart: number, selectionEnd: number, targets: { start: Element; end: Element }) => {
            const isValidSelection = selectionEnd - selectionStart + 1 <= LuisConstants.CustomText.MaximumNumberOfLabelChars;
            if (mode.mode === 'menu') {
                store.setDropdownData({
                    target: targets.end,
                    selection: [selectionStart, selectionEnd],
                    cancelSelectionCB: () => labelerRef.current.selectionStore.cancelSelection()
                });
            } else {
                onAddLabel({ entityId: mode.entityId, startCharIndex: selectionStart, endCharIndex: selectionEnd });
                labelerRef.current.selectionStore.cancelSelection();
            }

            if (!isValidSelection) {
                onInvalidSelection();
            }
        },
        [onAddLabel, store, mode]
    );

    const handleShapeClick = React.useCallback(
        (annotation: AnnotationData, event: React.MouseEvent<SVGElement, MouseEvent>) => {
            store.setDropdownData({
                target: event.target as Element,
                selection: [annotation.startToken, annotation.endToken]
            });
        },
        [store]
    );

    const tokenStoreFactory = (token: string, index: number) =>
        new ExtractionLabelerTokenStore(index, token, endIndicesLabelMap.get(index));

    const keyboardControl = (ev: KeyboardEvent) => {
        switch (ev.key) {
            case LuisKeyCodes.ArrowLeft:
                ev.stopPropagation();
                break;
            case LuisKeyCodes.ArrowRight:
                ev.stopPropagation();
                break;
            default:
                break;
        }
    };
    useEffect(() => {
        (wrapperRef.current?.childNodes[0] as HTMLDivElement)?.addEventListener('keydown', keyboardControl);
        return () => {
            (wrapperRef.current?.childNodes[0] as HTMLDivElement)?.removeEventListener('keydown', keyboardControl);
        };
    }, [wrapperRef]);
    return (
        <LabelerWrapper className={className} customProps={{ isRtl }} originalRef={wrapperRef}>
            <Labeler
                text={text}
                ref={labelerRef}
                annotations={annotations}
                onSelectionChange={handleSelect}
                tokenStoreFactory={tokenStoreFactory}
                key={`${text} ${isRtl} ${isWordTokenized}`}
                onAnnotationClick={canWrite ? handleShapeClick : noop}
                labelerConfigs={{
                    isRtl,
                    enableVirtualization: true,
                    isDisabled: isExtractionLabelerDisabled || !canWrite,
                    tokenizationType: isWordTokenized ? 'word' : 'character'
                }}
                onTokenRender={(tokenProps: ITokenRendererProps<ExtractionLabelerTokenStore>, defaultRenderer) =>
                    onTokenRender({
                        tokenProps,
                        defaultRenderer,
                        labels,
                        onConfirmAutoTags,
                        onRemoveLabel,
                        projectId: rootStore.currentCustomTextProjectStore.id,
                        isExtractionLabelerDisabled
                    })
                }
            />

            <Observer>
                {() =>
                    store.dropdownData ? (
                        <ExtractionEntityDropdown
                            schema={schema}
                            onAddLabel={onAddLabel}
                            onRemoveLabel={onRemoveLabel}
                            data={store.dropdownData}
                            labels={transformedLabels}
                            onDismiss={() => store.setDropdownData(null)}
                            onCreateNewEntity={onCreateNewEntity}
                        />
                    ) : null
                }
            </Observer>

            {mode.mode === 'brush' && (
                <EntityBrush entity={schema.find(entity => entity.id === mode.entityId)} attachedToRef={wrapperRef} />
            )}
        </LabelerWrapper>
    );
});
